import * as React from 'react'
import { useState } from 'react'
import { Tag } from 'lucide-react'
import { Input } from 'src/component/ui/input/input'
import { OutlinedButton } from 'src/component/ui/button'
import { PromoCodeNotice } from 'src/component/profile/promo-code-notice'
import { coreService } from 'src/service/core-service'
import { promoCodeService, ResolvedPromoCode } from 'src/service/promo-code-service'

type PromoCodeFieldProps = {
  promo: ResolvedPromoCode | null
  // Optional trailing note on the applied notice, e.g. "Monthly plan only".
  scopeNote?: string | null
  onApplied: (promo: ResolvedPromoCode) => void
  onRemoved: () => void
}

export const PromoCodeField = ({ promo, scopeNote, onApplied, onRemoved }: PromoCodeFieldProps): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false)
  const [code, setCode] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const [invalidCode, setInvalidCode] = useState<string | null>(null)

  const applyCode = async (): Promise<void> => {
    const trimmed = code.trim()
    if (!trimmed || isValidating) return

    setIsValidating(true)
    setInvalidCode(null)
    try {
      const { valid, discountLabel, appliesToProductIds } = await coreService.validatePromoCode(trimmed)
      if (!valid) {
        setInvalidCode(trimmed)
        return
      }
      promoCodeService.store({ code: trimmed, discountLabel, appliesToProductIds })
      const stored = promoCodeService.get()
      if (stored) onApplied(stored)
      setCode('')
      setIsOpen(false)
    } finally {
      setIsValidating(false)
    }
  }

  if (promo) {
    return (
      <PromoCodeNotice
        variant="applied"
        code={promo.code}
        discountLabel={promo.discountLabel}
        scopeNote={scopeNote}
        onRemove={onRemoved}
      />
    )
  }

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 self-start font-roboto-condensed text-sm tracking-[0.5px] text-mushroom underline underline-offset-2 hover:text-ivory transition-colors"
      >
        <Tag className="h-4 w-4 shrink-0" aria-hidden="true" />
        Have a promo code?
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          size="sm"
          type="text"
          placeholder="Promo code"
          autoFocus
          value={code}
          onChange={(e) => {
            setCode(e.target.value)
            setInvalidCode(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void applyCode()
            }
          }}
          variant={invalidCode ? 'error' : 'default'}
          className="w-auto min-w-[180px] font-roboto-condensed uppercase tracking-[1px]"
        />
        <OutlinedButton
          size="sm"
          onClick={() => void applyCode()}
          disabled={!code.trim() || isValidating}
          isLoading={isValidating}
        >
          APPLY
        </OutlinedButton>
        <button
          type="button"
          onClick={() => {
            setIsOpen(false)
            setCode('')
            setInvalidCode(null)
          }}
          className="font-roboto-condensed text-sm tracking-[0.5px] text-mushroom underline underline-offset-2 hover:text-ivory transition-colors"
        >
          Cancel
        </button>
      </div>
      {invalidCode && <PromoCodeNotice variant="invalid" code={invalidCode} />}
    </div>
  )
}
